import {
    Chip,
    Box,
    TextField,
    Grid, Button, FormControl, InputLabel, Select, MenuItem, FormHelperText, Autocomplete,
} from "@mui/material";
import {Controller} from "react-hook-form";
import {useTranslation} from "react-i18next";
import OutlinedInput from "@mui/material/OutlinedInput";
import {useBackgroundForm} from "../hooks/useBackgroundForm";
import Preloader from "../../../components/Preloader";
import {useGetLanguages} from "../../../hooks/useGetLanguages";
import {useGetNationalities} from "../../../hooks/useGetNationalities";
import {useGetCountries} from "../../../hooks/useGetCountries";
import {useGetEthnicities} from "../../../hooks/useGetEthnicities";
import {useApplicationStorage} from "../../../hooks/useApplicationStorage";

export const BackgroundForm = (props) => {
    const {data, onFinish} = props;
    const {t} = useTranslation();

    const {isLoading: isLoadingLanguages, languagesOptions} = useGetLanguages();
    const {isLoading: isLoadingNationalities, nationalitiesOptions} = useGetNationalities();
    const {isLoading: isLoadingCountries, countriesOptions} = useGetCountries();
    const {isLoading: isLoadingEthnicities, ethnicitiesOptions} = useGetEthnicities();

    const {leadData} = useApplicationStorage();

    const {
        handleSubmit, control, errors, isDirty,
    } = useBackgroundForm(data);

    const onSubmit = (dataForm) => {
        const payload = {...dataForm, leadId: leadData?.leadId};
        onFinish?.(payload, isDirty);
    };

    return (
        <Preloader isLoading={isLoadingLanguages || isLoadingNationalities || isLoadingCountries || isLoadingEthnicities} overflow>
            <form className="Students-form" onSubmit={handleSubmit(onSubmit)}>
                <Grid container rowSpacing={3} columnSpacing={2}>
                    <Grid item size={{xs: 12, md: 6}}>
                        <FormControl fullWidth error={!!errors.firstLanguage}>
                            <InputLabel required>{t("students.background.firstLanguage")}</InputLabel>
                            <Controller
                                name="firstLanguage"
                                control={control}
                                render={({field}) => (
                                    <Autocomplete
                                        {...field}
                                        value={languagesOptions.find((opt) => opt.value === field.value) || null}
                                        onChange={(event, newValue) => {
                                            field.onChange(newValue?.value || null);
                                        }}
                                        disablePortal
                                        options={languagesOptions}
                                        renderInput={(params) => <TextField {...params} placeholder={t("students.background.placeholders.firstLanguage")} />}
                                    />
                                )}
                            />
                            {errors.firstLanguage?.message ? (
                                <FormHelperText>{errors.firstLanguage.message}</FormHelperText>
                            ) : null}
                        </FormControl>
                    </Grid>
                    <Grid item size={{xs: 12, md: 6}}>
                        <FormControl fullWidth error={!!errors.nationality}>
                            <InputLabel required>{t("students.background.nationality")}</InputLabel>
                            <Controller
                                name="nationality"
                                control={control}
                                render={({field}) => (
                                    <Autocomplete
                                        {...field}
                                        value={nationalitiesOptions.find((opt) => opt.value === field.value) || null}
                                        onChange={(event, newValue) => {
                                            field.onChange(newValue?.value || null);
                                        }}
                                        disablePortal
                                        options={nationalitiesOptions}
                                        renderOption={(props, option, {index}) => (
                                            <li {...props} key={`${option.value}-${index}`}>
                                                {option.label}
                                            </li>
                                        )}
                                        renderInput={(params) => <TextField {...params} placeholder={t("students.background.placeholders.nationality")} />}
                                    />
                                )}
                            />
                            {errors.nationality?.message ? (
                                <FormHelperText>{errors.nationality.message}</FormHelperText>
                            ) : null}
                        </FormControl>
                    </Grid>
                    <Grid item size={{xs: 12, md: 6}}>
                        <FormControl fullWidth error={!!errors.countryOfResidence}>
                            <InputLabel required>{t("students.background.countryOfResidence")}</InputLabel>
                            <Controller
                                name="countryOfResidence"
                                control={control}
                                render={({field}) => (
                                    <Autocomplete
                                        {...field}
                                        value={countriesOptions.find((opt) => opt.value === field.value) || null}
                                        onChange={(event, newValue) => {
                                            field.onChange(newValue?.value || null);
                                        }}
                                        disablePortal
                                        options={countriesOptions}
                                        renderInput={(params) => <TextField {...params} placeholder={t("students.background.placeholders.countryOfResidence")} />}
                                    />
                                )}
                            />
                            {errors.countryOfResidence?.message ? (
                                <FormHelperText>{errors.countryOfResidence.message}</FormHelperText>
                            ) : null}
                        </FormControl>
                    </Grid>
                    <Grid item size={{xs: 12, md: 6}}>
                        <FormControl fullWidth error={!!errors.ethnicities}>
                            <InputLabel id="ethnicities-label" required>{t("students.background.ethnicitySelectAll")}</InputLabel>
                            <Controller
                                name="ethnicities"
                                control={control}
                                render={({field}) => (
                                    <Select
                                        {...field}
                                        labelId="ethnicities-label"
                                        label={t("students.background.ethnicitySelectAll")}
                                        input={<OutlinedInput id="select-multiple-chip" />}
                                        multiple
                                        value={field.value || []}
                                        onChange={(event) => field.onChange(event.target.value)}
                                        renderValue={(selected) => {
                                            if (!selected || selected.length === 0) {
                                                return <em className="select-placeholder">{t("students.background.placeholders.ethnicity")}</em>;
                                            }

                                            return (<Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                                                {selected.map((value) => {
                                                    const option = ethnicitiesOptions.find(o => o.value === value);
                                                    return (
                                                        <Chip
                                                            key={value}
                                                            label={option?.label ?? value}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                            );
                                        }}
                                    >
                                        {ethnicitiesOptions.map((item) => (
                                            <MenuItem key={item.value} value={item.value}>
                                                {item.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            {errors.ethnicities?.message ? (
                                <FormHelperText>{errors.ethnicities.message}</FormHelperText>
                            ) : null}
                        </FormControl>
                    </Grid>
                    <Grid item size={12} className="w-full flex">
                        <Button
                            variant="accent"
                            type="submit"
                            className="ml-auto"
                        >
                            {t("students.addInformation")}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Preloader>
    );
};
